import type { ProductHit } from '@libs/algolia';
import { Locale } from '@libs/core';
import { getLocale } from '@libs/core/utils';

import type {
    PersonalizedProduct,
    PersonalizeFormInputsDictionary,
    ProductDeliveryMethods,
} from '@/types/OrderDetails';

export const inputsBuilder = (data?: number[], required?: boolean) =>
    data?.map((item, index) => {
        const isRequired = required && index === 0;

        return {
            label: `Line ${index + 1}`,
            required: isRequired,
            requiredLabel: isRequired ? '(required)' : undefined,
            displayLength: true,
            optionalLabel: '',
            value: '',
            maxLength: item,
        };
    }) ?? [];

export const personalizedProductMapper = (product: ProductDeliveryMethods): PersonalizedProduct => {
    const isPersonalized = !!product.personalization?.templateId;

    const personalizationInputs: PersonalizeFormInputsDictionary = {};

    personalizationInputs.front = inputsBuilder(
        product.personalization?.textLocations?.frontLines,
        product.isCustomizationMandatory
    );
    personalizationInputs.back = inputsBuilder(
        product.personalization?.textLocations?.backLines,
        product.isCustomizationMandatory
    );

    const personalizationFields = {
        templateId: product.personalization?.templateId ?? 0,
        customizationType: product.personalization?.customizationType ?? '',
    };

    return {
        isPersonalized,
        personalizationFields,
        personalizationInputs,
    };
};

export const getProductDeliveryEligibility = (productDeliveryMethods?: ProductDeliveryMethods) => {
    const isInStoreOnly =
        typeof productDeliveryMethods?.inStoreOnlyOveride !== 'undefined'
            ? productDeliveryMethods.inStoreOnlyOveride
            : Boolean(productDeliveryMethods?.inStoreOnly);

    const isShipToHomeSupported =
        getLocale() === Locale['en-CA']
            ? Boolean(productDeliveryMethods?.['shipToHome-isCASupported'])
            : !isInStoreOnly;

    const isAutoshipAvailable = !!productDeliveryMethods?.isSubscriptionEnabled;

    return {
        bopisDisabled: !productDeliveryMethods?.bopisEligible || Boolean(productDeliveryMethods.onlineOnly),
        isSddDisabled:
            Boolean(productDeliveryMethods?.onlineOnly) || !productDeliveryMethods?.customScheduledDeliveryEligible,
        isInStoreOnly,
        isShipToHomeSupported,
        isAutoshipAvailable,
        isAutoshipSupported: (isShipToHomeSupported && isAutoshipAvailable) || (isAutoshipAvailable && !isInStoreOnly),
    };
};

export const getCaseRequiredQuantity = (
    remainingStock: number,
    requestedQty: number,
    customCaseQuantity: string | number
): number => {
    const caseQty = Number(customCaseQuantity);

    if (remainingStock < caseQty) {
        return 0;
    }

    if (requestedQty >= caseQty && requestedQty <= remainingStock) {
        return requestedQty;
    }

    return caseQty; // Return minimum case quantity instead of full remaining stock
};

/**
 * Maps a ProductHit from Algolia to ProductDeliveryMethods format
 */
export const mapProductHitToDeliveryMethods = (productHit: ProductHit): ProductDeliveryMethods => ({
    sku: productHit.objectID ?? '',
    inStoreOnly: productHit['shoppingOptions-isInStoreOnly'] ?? false,
    inStoreOnlyOveride: productHit['shoppingOptions-isInStoreOnlyOverride'] ?? false,
    bopisEligible: productHit['shoppingOptions-isBopisEligible'] ?? false,
    onlineOnly: productHit['shoppingOptions-isSoldOnlineOnly'] ?? false,
    isSubscriptionEnabled: productHit.isSubscriptionEnabled ?? false,
    customScheduledDeliveryEligible: productHit.customScheduledDeliveryEligible ?? false,
    'shipToHome-isCASupported': productHit['shipToHome-isCASupported'] ?? false,
    personalization: productHit.personalization,
    isCustomizationMandatory: productHit.isCustomizationMandatory ?? false,
});
